import { makeAssistantToolUI } from '@assistant-ui/react'
import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  addContributionToActive,
  openAsExploration,
} from '@/components/agent/tools/_shared/applyContribution'
import {
  pickActions,
  type ToolActionHandlers,
  type ToolActionKind,
} from '@/components/agent/tools/_shared/chat-tool-actions'
import { useChatToolContext } from '@/components/agent/tools/_shared/useChatToolContext'
import {
  type CandidateGraphActions,
  FindEntityByNameView,
} from '@/components/agent/tools/FindEntityByNameView'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { useSelectionStore } from '@/stores/selection-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import {
  type EntityCandidate,
  type FindEntityByNameArgs,
  type FindEntityByNameResult,
  parseResult,
  primaryLabel,
} from './find-entity-by-name'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface FindEntityByNameRenderProps {
  args: FindEntityByNameArgs
  result?: FindEntityByNameResult | string
  status: Status
}

/** Actions this tool can execute for a candidate — the menu `pickActions`
 *  chooses from. Kept in sync with `buildHandlers` below. */
const AVAILABLE_ACTIONS: ReadonlySet<ToolActionKind> = new Set<ToolActionKind>([
  'showOnGraph',
  'showSeparately',
  'addToGraph',
  'highlight',
])

/** One candidate as a single-node GraphView, the unit both canvas actions
 *  (fresh exploration / additive merge) operate on. */
function candidateView(caseId: string, c: EntityCandidate): GraphView {
  return {
    meta: { source: 'assistant', createdAt: new Date().toISOString(), caseId, label: c.name },
    nodes: [
      {
        id: c.element_id,
        labels: c.labels,
        typeLabel: primaryLabel(c.labels) ?? 'any',
        name: c.name,
        properties: { ...(c.properties ?? {}), name: c.name },
      },
    ],
    edges: [],
    view: emptyViewState(),
  }
}

/**
 * Connected `find_entity_by_name` tool card: parses the backend payload,
 * resolves per-candidate canvas actions against the current object set, and
 * hands pure data to FindEntityByNameView.
 *
 * Actions are decided per row rather than per card because each candidate
 * answers "is this already on canvas?" differently — an entity already in the
 * object set wants highlighting, one outside it wants adding, and with no
 * object set at all the only sensible move is opening a fresh exploration.
 * That is the same matrix `pickActions` applies to card footers, just fed one
 * entity id at a time.
 */
function FindEntityByNameRender({ args, result, status }: FindEntityByNameRenderProps) {
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()
  const ctx = useChatToolContext()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = parsed?.status === 'error' || status.type === 'incomplete'

  const buildHandlers = useCallback(
    (c: EntityCandidate): ToolActionHandlers => {
      if (!caseId) return {}

      // Fresh exploration — drops the current viewpoint. Offered as the only
      // action when nothing is active, and as the escape hatch ("Show
      // separately") when the current graph is too busy to read.
      const explore = () => {
        const store = getOrCreateInvestigationsStore({ caseId })
        openAsExploration({ store, view: candidateView(caseId, c), label: c.name })
        navigate(`/cases/${caseId}/graph`)
      }

      return {
        showOnGraph: explore,
        showSeparately: explore,
        // Additive merge into the active viewpoint, registered as a
        // removable chip. Leaves everything already on canvas in place.
        addToGraph: () => {
          const store = getOrCreateInvestigationsStore({ caseId })
          addContributionToActive({ store, view: candidateView(caseId, c), label: c.name })
          navigate(`/cases/${caseId}/graph`)
        },
        // Already in the object set: select it and ask the canvas to bring it
        // into view. Touches no object set — nothing is created, merged or
        // replaced.
        highlight: () => {
          useSelectionStore.getState().focusEntity(c.element_id, 'graph')
          navigate(`/cases/${caseId}/graph`)
        },
      }
    },
    [caseId, navigate]
  )

  const graphActionsFor = useCallback(
    (c: EntityCandidate): CandidateGraphActions | null => {
      if (!ctx) return null
      const { primary, secondary } = pickActions([c.element_id], AVAILABLE_ACTIONS, ctx)
      if (!primary) return null
      return { primary, secondary, handlers: buildHandlers(c) }
    },
    [ctx, buildHandlers]
  )

  // Open a single geolocated candidate on the map. Builds a one-node
  // exploration carrying `_geo_lat`/`_geo_lon` (the keys the geolocation map
  // reads) and navigates to the map view, which auto-fits to it.
  const onShowOnMap = (c: EntityCandidate, coords: { lat: number; lon: number }) => {
    if (!caseId) return
    const view = candidateView(caseId, c)
    view.nodes[0].properties = { _geo_lat: coords.lat, _geo_lon: coords.lon, name: c.name }
    const store = getOrCreateInvestigationsStore({ caseId })
    openAsExploration({ store, view, label: c.name })
    navigate(`/cases/${caseId}/geolocation`)
  }

  return (
    <FindEntityByNameView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      graphActionsFor={graphActionsFor}
      onShowOnMap={onShowOnMap}
    />
  )
}

export const FindEntityByNameToolUI = makeAssistantToolUI<
  FindEntityByNameArgs,
  FindEntityByNameResult | string
>({
  toolName: 'find_entity_by_name',
  render: FindEntityByNameRender,
})
